import { normalize, schema } from 'normalizr'
import {
  IAvailabilityDTO,
  IAutochargeConfigResponseDTO,
  IDepositRiderWalletDTO,
  IMembershipObject,
  IRiderLocationObject,
  IRiderWithServiceNotification,
  ITripAvailableProduct,
  ISearchRider,
  IRiderObject,
  IRiderChangeLogDTO
} from '../types'
import Paths from '../utils/paths'
import { serverApi } from '../utils/request'

class RiderService {
  private schemaAssistances = new schema.Entity('assistances')
  private schemaLocations = new schema.Entity('locations')
  private schemaMemberships = new schema.Entity('serviceMemberships')
  private schemaMobilities = new schema.Entity('mobilities')
  private schemaPaymentSources = new schema.Entity(
    'sources',
    {},
    {
      processStrategy: entity => ({
        id: entity.id,
        cardHolderName: entity.name,
        expMonth: entity.exp_month,
        expYear: entity.exp_year,
        isPrimary: entity.isDefault,
        lastFourNumbers: entity.last4,
        stripeCustomerId: entity.customer
      })
    }
  )
  private schemaWallets = new schema.Entity(
    'wallet',
    {},
    {
      processStrategy: entity => ({
        id: entity.id,
        balance: entity.balance,
        bonusBalance: entity.bonusBalance,
        currency: entity.currency,
        stripeCustomerId: entity.stripeCustomerId,
        riderId: entity.userId
      })
    }
  )
  private schemaRider = new schema.Entity('rider', {
    assistances: [this.schemaAssistances],
    locations: [this.schemaLocations],
    mobilities: [this.schemaMobilities]
    // serviceMemberships: [this.schemaMemberships]
  })

  public suspendMembership(membership: IMembershipObject) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(membership)
      }, 1000)
    })
  }

  public async addFundsToWallet(
    riderId: string,
    riderName: string,
    riderEmail: string,
    paymentSourceId: string,
    amountInDollars: number,
    currentUserId: string
  ) {
    const amount = Math.round(amountInDollars * 100)
    const dto: IDepositRiderWalletDTO = {
      currentUserId,
      userId: riderId,
      riderName,
      riderEmail,
      source: paymentSourceId,
      amount
    }
    return serverApi.post(`${Paths.payment.depositBalance}`, dto).then(({ data }) => data)
  }

  public async refundPayment(params: any) {
    return serverApi.post(`${Paths.payment.refundPayment}`, params).then(({ data }) => data)
  }

  public async getRefundedAmount(stripeTransactionId: string, walletId: string) {
    return serverApi
      .get(`${Paths.payment.refundedPayment}`, { params: { stripeTransactionId, walletId } })
      .then(({ data }) => data)
  }

  public async addBonusToWallet(riderId: string, amountInDollars: number, message: string) {
    const amount = Math.round(amountInDollars * 100)
    const dto: IDepositRiderWalletDTO = {
      userId: riderId,
      amount,
      message
    }

    return serverApi.post(`${Paths.payment.depositBonusBalance}`, dto).then(({ data }) => data)
  }

  public async deleteRider(id: string) {
    return await serverApi.delete(`${Paths.rider.base}/${id}`).then(({ data }) => data)
  }

  public async activateRider(id: string) {
    return await serverApi.put(`${Paths.rider.activate}/${id}`).then(({ data }) => data)
  }

  public async riderChangeLog(params: IRiderChangeLogDTO) {
    return await serverApi.get(`${Paths.rider.getRiderChangeLog}`, { params })
  }

  public async getRiderWallet(id: string) {
    return serverApi.get(`${Paths.payment.riderWallet}/${id}`).then(({ data }) => data)
  }

  public async getRiderAutochargeConfig(id: string) {
    return serverApi.get(`${Paths.payment.riderAutochargeConfig}/${id}`).then(({ data }) => data)
  }

  public async updateRiderAutochargeConfig(id: string, autochargeConfig: IAutochargeConfigResponseDTO) {
    autochargeConfig.userId = id
    return serverApi.post(`${Paths.payment.riderAutochargeConfig}/upsert`, autochargeConfig).then(({ data }) => data)
  }

  public async getRiderWalletTransactions(id: string) {
    return serverApi.get(`${Paths.payment.riderWalletTransactions}/${id}`).then(({ data }) => data)
  }

  public async rechargeWalletTransaction(walletTransactionId: string) {
    return serverApi.post(`${Paths.payment.rechargeWallet}`, { walletTransactionId }).then(({ data }) => data)
  }

  public async getRiderWalletTransactionHistory(id: string) {
    return serverApi.get(`${Paths.payment.riderWalletTransactionHistory}/${id}`).then(({ data }) => data)
  }

  public async addRiderPaymentSource(stripeToken: string, riderId: string) {
    return await serverApi
      .post(Paths.payment.addPaymentSource, {
        userId: riderId,
        source: stripeToken
      })
      .then(({ data }) => data)
  }

  public async updateDefaultPaymentSource(data: { source: string; userId: string }) {
    return await serverApi.post(Paths.payment.updateDefaultPaymentSource, data).then(({ data }) => data)
  }

  public async archiveRider(id: string) {
    const payload = {
      id,
      isArchived: true
    }
    return await serverApi.put(`${Paths.rider.updateArchiveStatus}`, payload).then(({ data }) => data)
  }

  public async restoreArchiveRider(id: string) {
    const payload = {
      id,
      isArchived: false
    }
    return await serverApi.put(`${Paths.rider.updateArchiveStatus}`, payload).then(({ data }) => data)
  }

  public async availableProducts(id: string, payload: IAvailabilityDTO) {
    const path = Paths.rider.availableProducts.replace(':riderId', id)
    return serverApi.post(path, payload).then(({ data }) => data)
  }

  public async createRider(dto: any) {
    const {
      additionalInfo: note,
      internalNote,
      birthDate,
      countryCode = '+1',
      email,
      firstName,
      lastName,
      notificationPreference = 'none',
      phone: phoneNumber,
      username,
      externalId,
      ownerOrganization,
      orcaSerialNumber,
      riderReferralCode,
      force,
      //Rider Dark Mode
      darkmode
    } = dto

    const riderDTO = {
      orcaSerialNumber,
      riderReferralCode,
      birthDate,
      firstName,
      lastName,
      note,
      internalNote,
      notificationPreference,
      username,
      externalId,
      ownerOrganization,
      //Rider Dark Mode
      darkmode,
      email: email ? { emailAddress: email } : null,
      phones: [
        {
          countryCode,
          phoneNumber,
          isPrimary: true
        }
      ],
      force
    }

    return await serverApi.post(Paths.rider.base, riderDTO).then(({ data }) => data)
  }

  public async deactivateRider(id: string) {
    return await serverApi.put(`${Paths.rider.deactivate}/${id}`).then(({ data }) => data)
  }

  public async getRiderAssistanceList(id: string) {
    return await serverApi.get(`${Paths.rider.getOneWithAssistances}/${id}`).then(({ data }) => {
      const { entities, result } = normalize(data, this.schemaRider)
      if (!entities || !result) {
        return { id: '', assistances: [] }
      }
      return {
        id: result,
        assistances: entities.rider[result].assistances
      }
    })
  }

  public async getMany(dto: any) {
    const resp: any = await serverApi.get(Paths.rider.base, {
      params: dto
    })
    return resp
  }
  public async getManyRiderJoinRequest(dto: any) {
    const resp: any = await serverApi.get(Paths.rider.base, {
      params: dto
    })
    return resp
  }

  public async searchRiders(dto: ISearchRider, options: { raw: boolean } = { raw: false }) {
    const resp: any = await serverApi.get(Paths.rider.findByFullNameOrPhoneNumber, {
      params: dto
    })

    if (options.raw) {
      return { riders: resp.data, total: resp.data.length }
    }

    const { entities, result } = normalize(resp.data, [this.schemaRider])
    if (!entities || !entities.rider) {
      return { riders: {}, total: 0 }
    }

    Object.keys(entities.rider).forEach((key: string) => {
      const rider = entities.rider[key]
      entities.rider[key] = {
        id: rider.id,
        additionalInfo: rider.note,
        birthDate: rider.birthDate,
        firstName: rider.firstName,
        lastName: rider.lastName,
        email: rider.email ? rider.email.emailAddress : null,
        phone: rider.phones[0] && rider.phones[0].phoneNumber,
        memberships: rider.serviceMemberships,
        username: rider.username,
        countryCode: rider.phones[0] && rider.phones[0].countryCode,
        notificationPreference: rider.notificationPreference,
        //Rider Dark Mode
        darkmode: rider.darkmode,
        disableMobileBooking: rider.disableMobileBooking ? rider.disableMobileBooking : false
      }
    })

    return { riders: entities.rider, total: resp.data.length }
  }

  public async getRiderList(orgId: string, isArchived: boolean, page?: number, recordsPerPage?: number) {
    return await serverApi
      .get(`${Paths.rider.getManyByOrganizationId}/${orgId}`, {
        params: {
          isArchived,
          page,
          recordsPerPage
        }
      })
      .then(({ data }: any) => {
        const { entities, result } = normalize(data.items, [this.schemaRider])
        if (!entities || !entities.rider) {
          return { riders: {}, total: 0 }
        }

        Object.keys(entities.rider).forEach((key: string) => {
          const rider = entities.rider[key]
          entities.rider[key] = {
            id: rider.id,
            additionalInfo: rider.note,
            birthDate: rider.birthDate,
            firstName: rider.firstName,
            lastName: rider.lastName,
            email: rider.email ? rider.email.emailAddress : null,
            phone: rider.phones[0] && rider.phones[0].phoneNumber,
            memberships: rider.serviceMemberships,
            username: rider.username,
            countryCode: rider.phones[0] && rider.phones[0].countryCode,
            notificationPreference: rider.notificationPreference,
            //Rider Dark Mode
            rider: rider.darkmode,
            disableMobileBooking: rider.disableMobileBooking ? rider.disableMobileBooking : false
          }
        })
        return { riders: entities.rider, total: data.total }
      })
  }

  public async getRiderLocationList(id: string) {
    return await serverApi.get(`${Paths.rider.getOneWithLocations}/${id}`).then(({ data }) => {
      const { entities, result } = normalize(data, this.schemaRider)
      if (!entities || !entities.rider || !entities.rider[result] || !result) {
        return { id: '', locations: [] }
      }
      return {
        id: result,
        locations: entities.locations
      }
    })
  }

  public async getRiderLocationActivityList(id: string) {
    return await serverApi.get(`${Paths.rider.getOneWithLocationActivities}/${id}`).then((v: any) => {
      return {
        id,
        locationActivity: v.data.data
      }
    })
  }

  public async getRiderMobilityOptionList(id: string) {
    return await serverApi.get(`${Paths.rider.getOneWithMobilities}/${id}`).then(({ data }) => {
      const { entities, result } = normalize(data, this.schemaRider)
      if (!entities || !entities.rider || !entities.rider[result] || !result) {
        return { id: '', mobilities: [] }
      }
      const rider = entities.rider[result]
      return {
        id: result,
        mobilities: rider.mobilities.map((id: string) => ({
          id,
          isPrimary: id === rider.primaryMobilityId
        }))
      }
    })
  }

  public async getRiderPersonalInfo(id: string) {
    const response = await serverApi.get<any>(`${Paths.rider.getOneWithMobilitiesAndPersonalInfoById}/${id}`)
    return {
      ...response.data,
      email: response.data.email ? response.data.email.emailAddress : null,
      phone: response.data.phones[0] && response.data.phones[0].phoneNumber,
      countryCode: response.data.phones[0] && response.data.phones[0].countryCode,
      birthDate: response.data.phones[0] && response.data.birthDate
    }
  }

  public async getOneByUsername(username: string): Promise<any> {
    const response = await serverApi.get<any>(`${Paths.rider.getOneByUsername}/${username}`)
    return response.data
  }

  public async riderImport(dto: any): Promise<any> {
    const response = await serverApi.post(Paths.importProcess.riderImport, dto)
    return response.data
  }

  public async riderImportMemberships(dto: any): Promise<any> {
    const response = await serverApi.put(Paths.importProcess.riderImportMemberships, dto)
    return response.data
  }

  public async removeRiderPaymentSource(stripeCardId: string, userId: string) {
    return await serverApi.delete(Paths.payment.removePaymentSource, { params: { userId, stripeCardId } })
  }

  public async updateRiderAssistances(dto: any) {
    return await serverApi.put(Paths.rider.updateOneWithAssistances, dto).then(({ data }) => data)
  }

  public async updateRiderLocations(riderId: string, locations: IRiderLocationObject[]) {
    return await serverApi.put(Paths.rider.updateOneWithLocations, { id: riderId, locations }).then(({ data }) => data)
  }

  public async updateRiderMemberships(dto: any) {
    const memberships = dto.memberships.map((it: IMembershipObject) => {
      const membership = {
        externalRiderId: it.externalRiderId ? it.externalRiderId : null,
        serviceId: it.service ? it.service.id : it.serviceId,
        beginsAt: it.isLimitedByDate ? it.beginsAt : null,
        expiresAt: it.isLimitedByDate ? it.expiresAt : null,
        recurringType: it.isLimitedByRecurringMonths ? it.recurringMonths.join(',') : null,
        ridesNum: it.isLimitedByNumberOfRides ? it.ridesNum : null,
        ridesRangeType: it.isLimitedByNumberOfRides ? it.ridesRangeType : null,
        requestedAt: it.requestedAt || new Date(),
        status: 'NEW',
        notAllowedPCA: it.notAllowedPCA
      }
      if (it.id) {
        return {
          ...membership,
          id: it.id
        }
      }
      return membership
    })
    return await serverApi
      .put(Paths.rider.updateOneWithMemberships, { id: dto.id, memberships })
      .then(({ data }) => data)
  }

  public async updateRiderMobilities(dto: any) {
    return await serverApi.put(Paths.rider.updateOneWithMobilities, dto).then(({ data }) => ({
      id: dto.id,
      mobilities: dto.mobilities.map((id: string) => ({
        id,
        isPrimary: id === dto.primaryMobilityId
      }))
    }))
  }

  public async updateRiderPersonalInfo(dto: IRiderObject) {
    const {
      id,
      note,
      orcaSerialNumber,
      riderReferralCode,
      internalNote,
      birthDate,
      countryCode = '1',
      email,
      firstName,
      lastName,
      notificationPreference = 'none',
      phone: phoneNumber,
      username,
      externalId,
      //PaymentMode
      paymentMode,
      //Rider Dark Mode
      darkmode,
      disableMobileBooking,
      caregiver
    } = dto
    const riderDTO = {
      id,
      birthDate,
      orcaSerialNumber,
      riderReferralCode,
      firstName,
      lastName,
      note,
      internalNote,
      notificationPreference,
      username,
      externalId,
      //PaymentMode
      paymentMode,
      //Darkmode
      darkmode,
      disableMobileBooking: disableMobileBooking ? disableMobileBooking : false,
      email: email ? { emailAddress: email } : null,
      phones: [
        {
          countryCode,
          phoneNumber,
          isPrimary: true
        }
      ],
      caregiver
    }
    return await serverApi.put(Paths.rider.updateOneWithPersonalInfo, riderDTO).then(({ data }) => data)
  }

  public async riderImportExternalId(dto: any): Promise<any> {
    const response = await serverApi.put(Paths.importProcess.riderImportExternalId, dto)
    return response.data
  }
}

const riderService = new RiderService()

export { riderService }



// WEBPACK FOOTER //
// ./src/services/Rider.ts